import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { v4 } from 'uuid';

import { MAX_FILE_UPLOAD_BYTE } from '@app/constants/UpLoadModal';
import { usePDF } from '@app/hooks/usePDF';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import workspaceService from '@app/libs/workspaceService';
import { setExitRoute } from '@app/store/contract-setup/actions';
import { getEditingDocumentsFile } from '@app/store/transport-layer/document/editing-documents/file';
import { getFileBlobFromURL, getFileFromURL } from '@app/store/transport-layer/files';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';
import { getEmail, getName } from '@app/store/user/user/selectors';

export function useSetupContractByEditingDocument() {
  const history = useHistory();
  const { initialize } = useSigningState();
  const currentUserEmail = useSelector(getEmail);
  const loginUserName = useSelector(getName);
  const dispatch = useDispatch();
  const { setPDF, resetPDF } = usePDF();

  async function load(id: string, title: string) {
    resetPDF();
    const fileUrl = await getEditingDocumentsFile(workspaceService.getWorkspaceId(), id);
    const { size: fileSize } = await getFileBlobFromURL(fileUrl);

    // 편집문서의 용량이 5mb가 넘으면 서명요청을 할 수 없음
    if (fileSize <= MAX_FILE_UPLOAD_BYTE) {
      const { fileId, url }: { fileId: string; url: string } = await postDocumentUpload({
        url: fileUrl,
      });

      const { data: pdf } = await getFileFromURL(url);
      setPDF(pdf);

      dispatch(setExitRoute('/editing-documents'));

      initialize({
        attachments: [],
        document: {
          // (@DevSDK): document를 template data 핸들링하는데 사용할지 고려하기
          // FYI: https://modusign.slack.com/archives/C04902B1JF7/p1674694203898699
          title,
          pdfFile: {
            url,
            id: fileId,
          },
          pdfPassword: null,
        },
        fields: [],
        participants: [
          {
            type: 'REQUESTER',
            contact: currentUserEmail ?? '',
            name: loginUserName,
            id: v4(),
            dataLabel: v4(),
          },
          {
            type: 'SIGNER',
            locale: 'ko',
            contact: currentUserEmail ?? '',
            signingMethod: 'EMAIL',
            name: loginUserName,
            id: v4(),
            dataLabel: v4(),
            order: 1,
            index: 0,
          },
          {
            type: 'SIGNER',
            id: v4(),
            dataLabel: v4(),
            locale: 'ko',
            signingMethod: null,
            name: '',
            order: 2,
            index: 1,
          },
        ],
        verifications: [],
        ui: {
          isInOrder: true,
          insertingField: null,
          lastInsertedField: createInitialLastInsertedField(),
        },
      });

      pushDataLayer({ document: undefined });
      pushDataLayer({
        event: 'documentInitialized',
        document: {
          documentSource: 'editingDocument',
          startedAt: Date.now(),
        },
      });
    } else {
      history.push('/editing-documents');
      throw new Error(
        '서명 요청 문서의 최대 크기는 5MB입니다.\n크기를 줄이거나 다른 문서로\n이용해 주세요.'
      );
    }
  }

  return {
    load,
  };
}
